import { useState, useEffect, useCallback, RefObject } from 'react';
import { useIsVisible } from './useIsVisible';
import { isBrowser } from './isBrowser';

interface UseHeaderOffsetRefs {
    headerRef: RefObject<HTMLElement>;
    sitesNavRef: RefObject<HTMLElement>;
    policyNavRef?: RefObject<HTMLElement>;
    isPolicy?: boolean;
}

interface UseHeaderOffsetOptions {
    includeBuffer?: number;
    includePolicyNav?: boolean;
    onlyWhenNavVisible?: boolean;
}

export const useHeaderOffset = (
    refs: UseHeaderOffsetRefs,
    options: UseHeaderOffsetOptions = {}
) => {
    const {
        includeBuffer = 0,
        includePolicyNav = false,
        onlyWhenNavVisible = false,
    } = options;

    const { headerRef, sitesNavRef, policyNavRef, isPolicy } = refs;

    const [offset, setOffset] = useState<number>(includeBuffer);
    const isNavVisible = useIsVisible(headerRef);

    const calculateOffset = useCallback((): number | null => {
        if (!sitesNavRef?.current || !headerRef?.current) {
            return null;
        }

        const headerHeight =
            onlyWhenNavVisible && !isNavVisible
                ? 0
                : headerRef.current.offsetHeight;

        let totalOffset =
            sitesNavRef.current.offsetHeight + headerHeight + includeBuffer;

        if (includePolicyNav && isPolicy && policyNavRef?.current) {
            totalOffset += policyNavRef.current.offsetHeight;
        }

        return totalOffset;
    }, [
        sitesNavRef,
        headerRef,
        policyNavRef,
        isNavVisible,
        isPolicy,
        includeBuffer,
        includePolicyNav,
        onlyWhenNavVisible,
    ]);

    useEffect(() => {
        const newOffset = calculateOffset();
        if (newOffset !== null) {
            setOffset(newOffset);
        }
    }, [
        sitesNavRef,
        headerRef,
        policyNavRef,
        isNavVisible,
        isPolicy,
        includeBuffer,
        includePolicyNav,
        onlyWhenNavVisible,
    ]);

    useEffect(() => {
        if (!isBrowser) return;

        const handleResize = () => {
            const newOffset = calculateOffset();
            if (newOffset !== null) {
                setOffset(newOffset);
            }
        };

        window.addEventListener(`resize`, handleResize);
        return () => window.removeEventListener(`resize`, handleResize);
    }, [calculateOffset]);

    return offset;
};
